import * as THREE from 'three';

export interface VolumetricCloudConfig {
  radius: number;
  height: number;
  resolution: THREE.Vector3;
  qualityLevel: 'low' | 'medium' | 'high' | 'ultra';
}

export interface CloudSample {
  density: number;
  color: THREE.Color;
  opacity: number;
}

export interface NoiseParameters {
  scale: number;
  octaves: number;
  persistence: number;
  lacunarity: number;
  timeScale: number;
}

export class VolumetricCloud extends THREE.Group {
  private config: VolumetricCloudConfig;
  private cloudMesh: THREE.Mesh;
  private cloudMaterial: THREE.ShaderMaterial;
  
  // Volumetric properties
  private densityField: Float32Array;
  private densityTexture: THREE.Data3DTexture;
  
  // Visual properties
  private baseColor: THREE.Color = new THREE.Color(0x4488ff);
  private edgeGlow: THREE.Color = new THREE.Color(0x8844ff);
  private absorption: number = 0.1;
  private scattering: number = 0.8;
  
  // Noise parameters
  private noise: NoiseParameters = {
    scale: 0.05,
    octaves: 4,
    persistence: 0.5,
    lacunarity: 2.0,
    timeScale: 0.1,
  };
  
  // Animation
  private time: number = 0;
  private lodLevel: 'low' | 'medium' | 'high' = 'high';
  
  constructor(config: VolumetricCloudConfig) {
    super();
    
    this.config = config;
    this.userData = { id: 'volumetric-cloud', type: 'effect' };
    
    this.createDensityField();
    this.createCloudMaterial();
    this.createCloudMesh();
    
    this.updateDensityField(0.1, 0.1); // Initial low density
  }
  
  private createDensityField(): void {
    const { x, y, z } = this.config.resolution;
    const size = x * y * z;
    
    this.densityField = new Float32Array(size);
    
    // Create 3D texture for density
    this.densityTexture = new THREE.Data3DTexture(
      this.densityField,
      x, y, z
    );
    this.densityTexture.format = THREE.RedFormat;
    this.densityTexture.type = THREE.FloatType;
    this.densityTexture.minFilter = THREE.LinearFilter;
    this.densityTexture.magFilter = THREE.LinearFilter;
    this.densityTexture.wrapS = THREE.ClampToEdgeWrapping;
    this.densityTexture.wrapT = THREE.ClampToEdgeWrapping;
    this.densityTexture.wrapR = THREE.ClampToEdgeWrapping;
    this.densityTexture.needsUpdate = true;
  }
  
  private createCloudMaterial(): void {
    const vertexShader = `
      varying vec3 vPosition;
      varying vec3 vWorldPosition;
      varying vec3 vNormal;
      
      void main() {
        vPosition = position;
        vWorldPosition = (modelMatrix * vec4(position, 1.0)).xyz;
        vNormal = normalize(normalMatrix * normal);
        
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `;
    
    const fragmentShader = `
      uniform sampler3D densityTexture;
      uniform vec3 lightDir;
      uniform vec3 viewPos;
      uniform float time;
      uniform vec3 baseColor;
      uniform vec3 edgeGlow;
      uniform float absorption;
      uniform float scattering;
      uniform float radius;
      uniform float height;
      uniform vec3 noiseParams; // scale, octaves, persistence
      uniform vec2 noiseParams2; // lacunarity, timeScale
      uniform float congestion;
      uniform float turbulence;
      
      varying vec3 vPosition;
      varying vec3 vWorldPosition;
      varying vec3 vNormal;
      
      // Simplex noise function
      vec3 mod289(vec3 x) {
        return x - floor(x * (1.0 / 289.0)) * 289.0;
      }
      
      vec4 mod289(vec4 x) {
        return x - floor(x * (1.0 / 289.0)) * 289.0;
      }
      
      vec4 permute(vec4 x) {
        return mod289(((x*34.0)+1.0)*x);
      }
      
      vec4 taylorInvSqrt(vec4 r) {
        return 1.79284291400159 - 0.85373472095314 * r;
      }
      
      float snoise(vec3 v) {
        const vec2 C = vec2(1.0/6.0, 1.0/3.0);
        const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
        
        vec3 i = floor(v + dot(v, C.yyy));
        vec3 x0 = v - i + dot(i, C.xxx);
        
        vec3 g = step(x0.yzx, x0.xyz);
        vec3 l = 1.0 - g;
        vec3 i1 = min(g.xyz, l.zxy);
        vec3 i2 = max(g.xyz, l.zxy);
        
        vec3 x1 = x0 - i1 + C.xxx;
        vec3 x2 = x0 - i2 + C.yyy;
        vec3 x3 = x0 - D.yyy;
        
        i = mod289(i);
        vec4 p = permute(permute(permute(
                   i.z + vec4(0.0, i1.z, i2.z, 1.0))
                 + i.y + vec4(0.0, i1.y, i2.y, 1.0))
                 + i.x + vec4(0.0, i1.x, i2.x, 1.0));
        
        float n_ = 0.142857142857;
        vec3 ns = n_ * D.wyz - D.xzx;
        
        vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
        
        vec4 x_ = floor(j * ns.z);
        vec4 y_ = floor(j - 7.0 * x_);
        
        vec4 x = x_ *ns.x + ns.yyyy;
        vec4 y = y_ *ns.x + ns.yyyy;
        vec4 h = 1.0 - abs(x) - abs(y);
        
        vec4 b0 = vec4(x.xy, y.xy);
        vec4 b1 = vec4(x.zw, y.zw);
        
        vec4 s0 = floor(b0)*2.0 + 1.0;
        vec4 s1 = floor(b1)*2.0 + 1.0;
        vec4 sh = -step(h, vec4(0.0));
        
        vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy;
        vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww;
        
        vec3 p0 = vec3(a0.xy, h.x);
        vec3 p1 = vec3(a0.zw, h.y);
        vec3 p2 = vec3(a1.xy, h.z);
        vec3 p3 = vec3(a1.zw, h.w);
        
        vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2, p2), dot(p3,p3)));
        p0 *= norm.x;
        p1 *= norm.y;
        p2 *= norm.z;
        p3 *= norm.w;
        
        vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
        m = m * m;
        return 42.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
      }
      
      // Fractal Brownian Motion
      float fbm(vec3 pos) {
        float value = 0.0;
        float amplitude = 1.0;
        float frequency = 1.0;
        
        for (int i = 0; i < 4; i++) {
          value += amplitude * snoise(pos * frequency);
          amplitude *= noiseParams.z; // persistence
          frequency *= noiseParams2.x; // lacunarity
        }
        
        return value;
      }
      
      float sampleDensity(vec3 pos) {
        // Convert to texture coordinates (0-1)
        vec3 texCoord = (pos + vec3(radius, height * 0.5, radius)) / vec2(radius * 2.0, height).xyx;
        
        // Sample base density from texture
        float baseDensity = texture(densityTexture, texCoord).r;
        
        // Add animated noise
        vec3 noisePos = pos * noiseParams.x + vec3(time * noiseParams2.y);
        float noise = fbm(noisePos);
        
        // Combine base density with noise
        float finalDensity = baseDensity * (1.0 + noise * turbulence);
        
        // Apply distance falloff (spherical)
        float dist = length(pos.xz) / radius;
        float heightFalloff = 1.0 - abs(pos.y) / (height * 0.5);
        float radialFalloff = 1.0 - smoothstep(0.3, 1.0, dist);
        
        finalDensity *= radialFalloff * heightFalloff;
        
        return clamp(finalDensity, 0.0, 1.0);
      }
      
      vec3 raymarch(vec3 ro, vec3 rd) {
        const int MAX_STEPS = 64;
        const float STEP_SIZE = 0.5;
        
        vec3 color = vec3(0.0);
        float totalDensity = 0.0;
        float t = 0.0;
        
        for (int i = 0; i < MAX_STEPS; i++) {
          vec3 pos = ro + rd * t;
          
          // Early exit if outside bounds
          if (length(pos.xz) > radius * 1.2 || abs(pos.y) > height * 0.6) {
            break;
          }
          
          float density = sampleDensity(pos);
          
          if (density > 0.01) {
            // Calculate lighting
            vec3 lightSample = pos + lightDir * 2.0;
            float lightDensity = sampleDensity(lightSample);
            float lightAttenuation = exp(-lightDensity * absorption);
            
            // Fee-based coloring
            float feeGradient = length(pos.xz) / radius;
            vec3 feeColor = mix(
              vec3(0.27, 0.53, 1.0), // Low fee blue
              mix(
                vec3(0.27, 1.0, 0.53), // Medium fee green
                mix(
                  vec3(1.0, 0.67, 0.27), // High fee orange
                  vec3(1.0, 0.27, 0.27), // Priority fee red
                  smoothstep(0.7, 1.0, feeGradient)
                ),
                smoothstep(0.4, 0.7, feeGradient)
              ),
              smoothstep(0.2, 0.4, feeGradient)
            );
            
            // Apply lighting and scattering
            vec3 scatterColor = feeColor * scattering * lightAttenuation;
            color += scatterColor * density * (1.0 - totalDensity) * STEP_SIZE;
            
            totalDensity += density * STEP_SIZE;
            
            if (totalDensity > 0.99) break;
          }
          
          t += STEP_SIZE;
        }
        
        // Add edge glow
        float edgeFactor = 1.0 - totalDensity;
        color += edgeGlow * edgeFactor * 0.1;
        
        return color;
      }
      
      void main() {
        vec3 viewDir = normalize(vWorldPosition - viewPos);
        vec3 rayOrigin = vWorldPosition;
        
        vec3 color = raymarch(rayOrigin, viewDir);
        
        // Apply congestion-based intensity
        color *= (0.5 + congestion * 0.5);
        
        gl_FragColor = vec4(color, length(color) * 0.8);
      }
    `;
    
    this.cloudMaterial = new THREE.ShaderMaterial({
      vertexShader,
      fragmentShader,
      uniforms: {
        densityTexture: { value: this.densityTexture },
        lightDir: { value: new THREE.Vector3(0, 1, 0) },
        viewPos: { value: new THREE.Vector3() },
        time: { value: 0 },
        baseColor: { value: this.baseColor },
        edgeGlow: { value: this.edgeGlow },
        absorption: { value: this.absorption },
        scattering: { value: this.scattering },
        radius: { value: this.config.radius },
        height: { value: this.config.height },
        noiseParams: { value: new THREE.Vector3(this.noise.scale, this.noise.octaves, this.noise.persistence) },
        noiseParams2: { value: new THREE.Vector2(this.noise.lacunarity, this.noise.timeScale) },
        congestion: { value: 0.1 },
        turbulence: { value: 0.1 },
      },
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.BackSide,
    });
  }
  
  private createCloudMesh(): void {
    // Create a sphere geometry for the cloud bounds
    const geometry = new THREE.SphereGeometry(this.config.radius, 32, 16);
    
    this.cloudMesh = new THREE.Mesh(geometry, this.cloudMaterial);
    this.cloudMesh.userData = { id: 'cloud-mesh', type: 'volumetric' };
    
    this.add(this.cloudMesh);
  }
  
  public updateDensityField(congestion: number, turbulence: number): void {
    const { x, y, z } = this.config.resolution;
    const radius = this.config.radius;
    const height = this.config.height;
    
    // Update density field based on congestion
    for (let i = 0; i < x; i++) {
      for (let j = 0; j < y; j++) {
        for (let k = 0; k < z; k++) {
          const index = i + j * x + k * x * y;
          
          // Convert to world coordinates
          const worldX = (i / x - 0.5) * radius * 2;
          const worldY = (j / y - 0.5) * height;
          const worldZ = (k / z - 0.5) * radius * 2;
          
          // Calculate distance from center
          const dist = Math.sqrt(worldX * worldX + worldZ * worldZ) / radius;
          const heightFactor = 1.0 - Math.abs(worldY) / (height * 0.5);
          
          // Base density based on distance
          let density = Math.max(0, 1.0 - dist) * heightFactor;
          
          // Apply congestion multiplier
          density *= congestion;
          
          // Add some noise for natural variation
          const noise = Math.sin(worldX * 0.1) * Math.cos(worldZ * 0.1) * Math.sin(worldY * 0.2);
          density += noise * 0.1 * turbulence;
          
          this.densityField[index] = Math.max(0, Math.min(1, density));
        }
      }
    }
    
    this.densityTexture.needsUpdate = true;
    
    // Update shader uniforms
    this.cloudMaterial.uniforms['congestion'].value = congestion;
    this.cloudMaterial.uniforms['turbulence'].value = turbulence;
  }
  
  public sampleDensity(position: THREE.Vector3): number {
    const { x, y, z } = this.config.resolution;
    
    // Convert world position to texture coordinates
    const texX = (position.x / this.config.radius + 1) * 0.5;
    const texY = (position.y / this.config.height + 1) * 0.5;
    const texZ = (position.z / this.config.radius + 1) * 0.5;
    
    // Clamp to texture bounds
    if (texX < 0 || texX > 1 || texY < 0 || texY > 1 || texZ < 0 || texZ > 1) {
      return 0;
    }
    
    // Convert to array indices
    const i = Math.floor(texX * (x - 1));
    const j = Math.floor(texY * (y - 1));
    const k = Math.floor(texZ * (z - 1));
    
    const index = i + j * x + k * x * y;
    return this.densityField[index] || 0;
  }
  
  public update(deltaTime: number): void {
    this.time += deltaTime;
    
    // Update shader time
    this.cloudMaterial.uniforms['time'].value = this.time;
    
    // Update camera position for lighting
    const camera = this.getCamera();
    if (camera) {
      this.cloudMaterial.uniforms['viewPos'].value.copy(camera.position);
    }
  }
  
  private getCamera(): THREE.Camera | null {
    let current: THREE.Object3D | null = this;
    while (current && !(current instanceof THREE.Scene)) {
      current = current.parent;
    }
    
    if (current instanceof THREE.Scene) {
      const cameras = current.children.filter(child => child instanceof THREE.Camera);
      return cameras.length > 0 ? cameras[0] as THREE.Camera : null;
    }
    
    return null;
  }
  
  public setLODLevel(level: 'low' | 'medium' | 'high'): void {
    this.lodLevel = level;
    
    // Adjust shader complexity based on LOD
    const stepCounts = {
      low: 32,
      medium: 48,
      high: 64,
    };
    
    // This would require shader recompilation in a real implementation
    // For now, we'll adjust the step size instead
    const stepSizes = {
      low: 1.0,
      medium: 0.75,
      high: 0.5,
    };
    
    // Update material properties
    this.cloudMaterial.needsUpdate = true;
  }
  
  public setQualityLevel(level: 'low' | 'medium' | 'high' | 'ultra'): void {
    this.config.qualityLevel = level;
    
    // Update visual quality
    const qualitySettings = {
      low: { absorption: 0.2, scattering: 0.4 },
      medium: { absorption: 0.15, scattering: 0.6 },
      high: { absorption: 0.1, scattering: 0.8 },
      ultra: { absorption: 0.05, scattering: 1.0 },
    };
    
    const settings = qualitySettings[level];
    this.absorption = settings.absorption;
    this.scattering = settings.scattering;
    
    this.cloudMaterial.uniforms['absorption'].value = this.absorption;
    this.cloudMaterial.uniforms['scattering'].value = this.scattering;
  }
  
  public setResolution(resolution: THREE.Vector3): void {
    this.config.resolution = resolution;
    
    // Recreate density field with new resolution
    this.createDensityField();
    this.cloudMaterial.uniforms['densityTexture'].value = this.densityTexture;
  }
  
  public dispose(): void {
    this.densityTexture.dispose();
    this.cloudMaterial.dispose();
    
    if (this.cloudMesh.geometry) {
      this.cloudMesh.geometry.dispose();
    }
  }
}